import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import { 
  Plus, 
  Edit2, 
  Trash2,
  Wallet,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  PiggyBank,
  ArrowUpCircle,
  ArrowDownCircle,
  BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category options based on transaction type
const INCOME_CATEGORIES = [
  'Match Dues',
  'Fines',
  'Sponsorship',
  'Fundraising',
  'Other Income'
];

const EXPENSE_CATEGORIES = [
  'Referee',
  'Pitch Hire',
  'Travel',
  'Equipment',
  'Kit',
  'Admin',
  'Other Expense'
];

export default function TeamKitty() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({ total_incoming: 0, total_outgoing: 0, balance: 0 });
  const [categoryBreakdown, setCategoryBreakdown] = useState({ income_by_category: {}, expense_by_category: {} });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    expense_type: 'incoming',
    category: ''
  });

  useEffect(() => {
    if (user?.current_season_id) {
      fetchExpenses();
      fetchSummary();
      fetchCategoryBreakdown();
    } else {
      setLoading(false);
    }
  }, [user?.current_season_id]);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/expenses`);
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/expenses/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const fetchCategoryBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/expenses/by-category`);
      setCategoryBreakdown(response.data);
    } catch (error) {
      console.error('Failed to fetch category breakdown:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        date: formData.date,
        description: formData.description,
        amount: parseFloat(formData.amount),
        expense_type: formData.expense_type,
        category: formData.category || null
      };

      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`${API}/expenses`, payload);
        toast.success('Expense added successfully');
      }
      fetchExpenses();
      fetchSummary();
      fetchCategoryBreakdown();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date,
      description: expense.description,
      amount: expense.amount.toString(),
      expense_type: expense.expense_type,
      category: expense.category || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success('Expense deleted successfully');
      fetchExpenses();
      fetchSummary();
      fetchCategoryBreakdown();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      expense_type: 'incoming',
      category: ''
    });
    setEditingExpense(null);
    setDialogOpen(false);
  };

  // Calculate running total for each expense
  const expensesWithRunningTotal = expenses.map((expense, index) => {
    const previousExpenses = expenses.slice(0, index + 1);
    const runningTotal = previousExpenses.reduce((total, e) => {
      return e.expense_type === 'incoming' ? total + e.amount : total - e.amount;
    }, 0);
    return { ...expense, runningTotal };
  });

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-season-message">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6 max-w-md">
            Please create or select a season to start tracking your team kitty.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="team-kitty-container">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
            Team Kitty
          </h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                size="icon"
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
                data-testid="add-expense-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold font-title">
                  {editingExpense ? 'Edit Transaction' : 'Add Transaction'}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {editingExpense ? 'Edit expense details' : 'Add a new income or expense'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="expense_type">Type</Label>
                  <Select
                    value={formData.expense_type}
                    onValueChange={(value) => setFormData({ ...formData, expense_type: value, category: '' })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="expense-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="incoming">
                        <span className="flex items-center gap-2">
                          <ArrowUpCircle className="w-4 h-4 text-emerald-600" />
                          Income
                        </span>
                      </SelectItem>
                      <SelectItem value="outgoing">
                        <span className="flex items-center gap-2">
                          <ArrowDownCircle className="w-4 h-4 text-red-600" />
                          Expense
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="expense-category-select">
                      <SelectValue placeholder="Select category (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {(formData.expense_type === 'incoming' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="rounded-xl"
                    data-testid="expense-date-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Match day fees, Kit purchase"
                    className="rounded-xl"
                    data-testid="expense-description-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (£)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="rounded-xl"
                    data-testid="expense-amount-input"
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-[#064E3B] hover:bg-[#065f46] text-white rounded-xl" data-testid="save-expense-btn">
                    {editingExpense ? 'Update' : 'Add'} Transaction
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="stat-card card-hover" data-testid="total-incoming">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-500">Total Income</p>
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600 mt-1">£{summary.total_incoming.toFixed(2)}</p>
                </div>
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 sm:w-7 sm:h-7 text-emerald-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover" data-testid="total-outgoing">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-500">Total Expenses</p>
                  <p className="text-2xl sm:text-3xl font-bold text-red-600 mt-1">£{summary.total_outgoing.toFixed(2)}</p>
                </div>
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-red-100 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 sm:w-7 sm:h-7 text-red-600" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover" data-testid="balance">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-500">Current Balance</p>
                  <p className={`text-2xl sm:text-3xl font-bold mt-1 ${summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    £{summary.balance.toFixed(2)}
                  </p>
                </div>
                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center ${summary.balance >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  <PiggyBank className={`w-5 h-5 sm:w-7 sm:h-7 ${summary.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transactions List */}
        <Card data-testid="transactions-list">
          <CardHeader>
            <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
              <Wallet className="w-5 h-5 text-gray-600" strokeWidth={1.5} />
              All Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading transactions...</div>
            ) : expensesWithRunningTotal.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-8 h-8 text-gray-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions Yet</h3>
                <p className="text-gray-500 mb-4">Start tracking your team's income and expenses</p>
                <Button
                  onClick={() => setDialogOpen(true)}
                  className="btn-primary rounded-xl"
                >
                  <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Add First Transaction
                </Button>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Description</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Category</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">In</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Out</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Balance</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expensesWithRunningTotal.map((expense) => (
                        <tr key={expense.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`expense-row-${expense.id}`}>
                          <td className="py-3 px-4 text-gray-700">{formatDateUK(expense.date)}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {expense.expense_type === 'incoming' ? (
                                <ArrowUpCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" strokeWidth={1.5} />
                              ) : (
                                <ArrowDownCircle className="w-4 h-4 text-red-600 flex-shrink-0" strokeWidth={1.5} />
                              )}
                              <span className="text-gray-900">{expense.description}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600 text-sm">
                            {expense.category || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {expense.expense_type === 'incoming' ? (
                              <span className="text-emerald-600 font-medium">£{expense.amount.toFixed(2)}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {expense.expense_type === 'outgoing' ? (
                              <span className="text-red-600 font-medium">£{expense.amount.toFixed(2)}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className={`font-bold ${expense.runningTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              £{expense.runningTotal.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(expense)}
                                className="p-2 h-auto"
                                data-testid={`edit-expense-${expense.id}`}
                              >
                                <Edit2 className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(expense.id)}
                                className="p-2 h-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-expense-${expense.id}`}
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="sm:hidden flex flex-col gap-3">
                  {expensesWithRunningTotal.map((expense) => (
                    <div 
                      key={expense.id} 
                      className="p-4 bg-gray-50 rounded-xl"
                      data-testid={`expense-card-${expense.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {expense.expense_type === 'incoming' ? (
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                              <ArrowUpCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                              <ArrowDownCircle className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{expense.description}</p>
                            <p className="text-xs text-gray-500">{expense.category || 'Uncategorized'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(expense)}
                            className="p-1.5 h-auto"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(expense.id)}
                            className="p-1.5 h-auto text-red-500"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                        <span className="text-xs text-gray-500">{formatDateUK(expense.date)}</span>
                        <div className="flex items-center gap-4">
                          <span className={`font-bold ${expense.expense_type === 'incoming' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {expense.expense_type === 'incoming' ? '+' : '-'}£{expense.amount.toFixed(2)}
                          </span>
                          <span className={`text-sm font-medium ${expense.runningTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            Bal: £{expense.runningTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        {(Object.keys(categoryBreakdown.income_by_category).length > 0 || Object.keys(categoryBreakdown.expense_by_category).length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-testid="category-breakdown">
            {/* Income by Category */}
            <Card className="stat-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold font-title flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  Income by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(categoryBreakdown.income_by_category).length === 0 ? (
                  <p className="text-gray-500 text-sm">No income recorded yet</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {Object.entries(categoryBreakdown.income_by_category)
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, amount]) => {
                        const percentage = summary.total_incoming > 0 
                          ? (amount / summary.total_incoming) * 100 
                          : 0;
                        return (
                          <div key={category} className="space-y-1" data-testid={`income-category-${category.toLowerCase().replace(/\s+/g, '-')}`}>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700">{category}</span>
                              <span className="text-sm font-medium text-emerald-600">£{amount.toFixed(2)}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expenses by Category */}
            <Card className="stat-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold font-title flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-red-600" strokeWidth={1.5} />
                  Expenses by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {Object.keys(categoryBreakdown.expense_by_category).length === 0 ? (
                  <p className="text-gray-500 text-sm">No expenses recorded yet</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {Object.entries(categoryBreakdown.expense_by_category)
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, amount]) => {
                        const percentage = summary.total_outgoing > 0 
                          ? (amount / summary.total_outgoing) * 100 
                          : 0;
                        return (
                          <div key={category} className="space-y-1" data-testid={`expense-category-${category.toLowerCase().replace(/\s+/g, '-')}`}>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700">{category}</span>
                              <span className="text-sm font-medium text-red-600">£{amount.toFixed(2)}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-red-500 rounded-full transition-all duration-500"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
